"use client";

import React from "react";
import { IlluminatedImageDiagram, IlluminatedImageHandle, IlluminatedImageMode } from "./Definitions";

interface AnimationData
{
    started: number;
    diagram?: IlluminatedImageDiagram;
}

export interface IlluminatedImageBaseProps extends React.ComponentProps<"object"> { }

const IlluminatedImageBase = React.forwardRef<IlluminatedImageHandle, IlluminatedImageBaseProps>((props, ref) =>
{
    const elementRef = React.useRef<HTMLObjectElement>(null);
    const animation = React.useRef<AnimationData | undefined>(undefined);

    const getMode = React.useCallback((elapsed: number, diagram?: IlluminatedImageDiagram): { mode: IlluminatedImageMode; final: boolean } =>
    {
        if (diagram === undefined)
        {
            return { mode: IlluminatedImageMode.Off, final: true };
        }

        const result = { mode: IlluminatedImageMode.Off, final: false };

        const initialDelay = diagram.initialDelay ?? 0;

        if (elapsed >= initialDelay)
        {
            elapsed -= initialDelay;

            const { onDuration, offDuration, count } = diagram;

            if (onDuration === undefined)
            {
                result.mode = IlluminatedImageMode.On;
                result.final = true;
            }
            else if (offDuration === undefined)
            {
                if (elapsed < onDuration)
                {
                    result.mode = IlluminatedImageMode.On;
                }
                else
                {
                    result.final = true;
                }
            }
            else
            {
                const period = onDuration + offDuration;

                if (count !== undefined && elapsed >= period * count)
                {
                    result.final = true;
                }
                else
                {
                    const offset = elapsed % period;
                    if (offset < onDuration)
                    {
                        result.mode = IlluminatedImageMode.On
                    }
                }
            }
        }

        result.mode = diagram.inverse ? [IlluminatedImageMode.On, IlluminatedImageMode.Off][result.mode] : result.mode;

        return result;
    }, []);

    const animate = React.useCallback((): void =>
    {
        const data = animation.current;
        const element = elementRef.current?.contentDocument?.documentElement;

        if (data !== undefined && element !== undefined)
        {
            const { started, diagram } = data;

            const modeEx = getMode(performance.now() - started, diagram);

            element.dataset.mode = ["off", "on"][modeEx.mode];

            if (modeEx.final)
            {
                animation.current = undefined;
            }
            else
            {
                requestAnimationFrame(animate);
            }
        }

    }, [getMode]);

    const requestAnimation = React.useCallback((diagram?: IlluminatedImageDiagram): void =>
    {
        const alreadyRequested = animation.current !== undefined;

        animation.current = {
            started: performance.now(),
            diagram,
        }

        if (!alreadyRequested)
        {
            requestAnimationFrame(animate);
        }
    }, [animate]);

    React.useImperativeHandle(ref, () => ({
        execute: diagram => requestAnimation(diagram),
        off: () => requestAnimation(),
        on: () => requestAnimation({}),
    }), [requestAnimation]);

    return <object {...props} ref={elementRef} />;
});

IlluminatedImageBase.displayName = "IlluminatedImageBase";
export { IlluminatedImageBase };

